import React, { useEffect, useState } from 'react';
import {
  Paper,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Chip,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  RestartAlt as RestartAltIcon,
} from '@mui/icons-material';
import { ownerService } from '../../services/ownerService';
import { progressStageSettingsService } from '../../services/progressStageSettingsService';
import { ProgressStage } from '../../services/progressStageService';
import {
  hireCodeRatioSettingsService,
  HireCodeRatioConfig,
  RatioKey,
  RATIO_KEYS,
  RATIO_GROUP_LABELS,
  RATIO_GROUP_DESCRIPTIONS,
} from '../../services/hireCodeRatioSettingsService';

interface HireCodeRatioSettingsProps {
  onClose?: () => void;
}

const HireCodeRatioSettings: React.FC<HireCodeRatioSettingsProps> = ({ onClose }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [orgId, setOrgId] = useState<number | null>(null);
  const [availableStages, setAvailableStages] = useState<ProgressStage[]>([]);
  const [config, setConfig] = useState<HireCodeRatioConfig>(hireCodeRatioSettingsService.getDefaultConfig());
  const [hasCustomSettings, setHasCustomSettings] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const currentOrgIdString = await ownerService.getOwnerOrgId();
      if (!currentOrgIdString) {
        setError('Unable to determine organization ID');
        return;
      }
      const currentOrgId = Number(currentOrgIdString);
      if (isNaN(currentOrgId)) {
        setError('Invalid organization ID');
        return;
      }
      setOrgId(currentOrgId);

      const stages = await progressStageSettingsService.getStages(currentOrgId);
      setAvailableStages(stages);

      const settings = await hireCodeRatioSettingsService.getSettings(currentOrgId);
      setConfig(settings);

      const custom = await hireCodeRatioSettingsService.hasCustomSettings(currentOrgId);
      setHasCustomSettings(custom);
    } catch (e: any) {
      setError(e.message || 'Failed to load hire/code ratio settings');
    } finally {
      setLoading(false);
    }
  };

  const toggleStage = (ratioKey: RatioKey, stageValue: number) => {
    setConfig(prev => {
      const current = prev[ratioKey];
      const next = current.includes(stageValue)
        ? current.filter(v => v !== stageValue)
        : [...current, stageValue].sort((a, b) => a - b);
      return { ...prev, [ratioKey]: next };
    });
  };

  const handleSaveAll = async () => {
    if (!orgId) return;
    try {
      setSaving(true);
      setError(null);
      await hireCodeRatioSettingsService.replaceAllSettings(orgId, config);
      setSuccess(true);
      setHasCustomSettings(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (e: any) {
      setError(e.message || 'Failed to save hire/code ratio settings');
    } finally {
      setSaving(false);
    }
  };

  const handleResetDefaults = () => {
    setConfig(hireCodeRatioSettingsService.getDefaultConfig());
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        mt: 3,
        borderRadius: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
          Hire / Code Settings
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {orgId ? `Organization ID: ${orgId}` : ''}
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          {!hasCustomSettings && (
            <Alert severity="info" sx={{ mb: 2 }}>
              You are currently viewing default ratio settings. Click "Save Changes" to save custom settings for your organization.
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Hire / Code ratio settings updated successfully!
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Define which progress stages are used to calculate each ratio card. Select the stages that should be included in each group.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {RATIO_KEYS.map((ratioKey) => (
              <Paper key={ratioKey} elevation={1} sx={{ p: 2.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {RATIO_GROUP_LABELS[ratioKey]}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  {RATIO_GROUP_DESCRIPTIONS[ratioKey]}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {availableStages.map((stage) => {
                    const selected = config[ratioKey].includes(stage.value);
                    return (
                      <Chip
                        key={stage.value}
                        label={`${stage.value} - ${stage.label}`}
                        onClick={() => toggleStage(ratioKey, stage.value)}
                        color={selected ? 'primary' : 'default'}
                        variant={selected ? 'filled' : 'outlined'}
                        sx={{
                          fontWeight: selected ? 600 : 400,
                          cursor: 'pointer',
                        }}
                      />
                    );
                  })}
                  {availableStages.length === 0 && (
                    <Typography variant="body2" color="textSecondary">
                      No progress stages configured. Please set up progress stages first.
                    </Typography>
                  )}
                </Box>
              </Paper>
            ))}
          </Box>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
            <Button
              startIcon={<RestartAltIcon />}
              onClick={handleResetDefaults}
              variant="outlined"
              disabled={saving}
            >
              Reset to Defaults
            </Button>
            {onClose && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button onClick={onClose} startIcon={<CancelIcon />} disabled={saving}>
                  Close
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveAll}
                  disabled={saving || loading}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default HireCodeRatioSettings;
